import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, LOCALE_ID, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { DevToolsPanel } from '@components/dev-tools';
import { environment } from '@environments/environment';
import { BrandId, DevToolsMode } from '@environments/model';
import { CURRENT_LANGUAGE } from '@shared/services/language';
import { LogsData } from '@shared/services/logs';
import { AppStore } from '@stores';
import { NgxSonnerToaster } from 'ngx-sonner';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  providers: [LogsData, { provide: LOCALE_ID, useFactory: () => CURRENT_LANGUAGE() }],
  imports: [RouterOutlet, NgxSonnerToaster, DevToolsPanel],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .dt-fab {
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 9999;
      width: 44px;
      height: 44px;
      border-radius: 14px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      color: #059669;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 200ms;
      box-shadow:
        0 4px 16px rgba(15, 23, 42, 0.06),
        0 1px 3px rgba(15, 23, 42, 0.04);
    }

    .dt-fab:hover {
      transform: scale(1.06);
      border-color: rgba(5, 150, 105, 0.2);
    }

    .dt-fab:focus-visible {
      outline: 2px solid #059669;
      outline-offset: 2px;
    }

    .dt-fab:active {
      transform: scale(0.94);
    }
  `,
})
export class App {
  private readonly appStore = inject(AppStore);
  readonly brand = this.appStore.select('branding');
  private readonly document = inject(DOCUMENT);
  private readonly titleService = inject(Title);

  readonly devToolsEnabled = signal(false);
  readonly devToolsOpen = signal(false);

  private readonly devToolsMode: DevToolsMode = environment.devTools;

  constructor() {
    this.initDevTools();

    effect(() => {
      const brandId = this.brand();
      const brandLabel = this.appStore.brandLabel();
      if (brandLabel) {
        this.titleService.setTitle(`evoq x ${brandLabel}`);
      }
      if (brandId) {
        this.updateFavicon(brandId);
        // Sync theme on <body> so CDK overlay components inherit brand styles
        this.document.body.setAttribute('data-theme', brandId);
      }
    });
  }

  private initDevTools(): void {
    if (this.devToolsMode === 'on') {
      this.devToolsEnabled.set(true);
    } else if (this.devToolsMode === 'query') {
      const params = new URLSearchParams(window.location.search);
      this.devToolsEnabled.set(params.has('debug'));
    }
  }

  private updateFavicon(brandId: BrandId): void {
    const existingLink = this.document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    const link = existingLink || this.document.createElement('link');
    link.type = 'image/svg+xml';
    link.rel = 'icon';
    link.href = `/favicons/evoq-${brandId}.svg`;
    if (!existingLink) {
      this.document.head.appendChild(link);
    }
  }
}
